/**
 * The ability slot's cooldown + stamina look (AD-849; docs/design/mobile-layout.md §7.7), shared by
 * the desktop bar, the compact (phone) bar and the T3 touch overlay. The rules are pure
 * (`abilitySlotState.ts`); this file is the clock and the DOM.
 */

import { useEffect, useState } from 'react';

import {
  abilitySlotView,
  anyCooling,
  type AbilityCooldowns,
  type AbilitySlotView,
} from './abilitySlotState.js';

/** How often the countdown redraws while a slot cools (ms) — smooth enough for a fill, cheap. */
export const SLOT_REDRAW_MS = 100;

/**
 * Every loadout slot's view, redrawn every {@link SLOT_REDRAW_MS} while any cooldown runs and not
 * at all otherwise (a ready bar costs no timer).
 */
export function useAbilitySlotViews(
  abilityIds: readonly string[],
  cooldowns: AbilityCooldowns,
  costs: readonly { id: string; staminaCost: number }[] | undefined,
  stamina: number | undefined,
): Readonly<Record<string, AbilitySlotView>> {
  const [now, setNow] = useState(() => Date.now());
  const cooling = anyCooling(cooldowns, now);
  useEffect(() => {
    // Re-read the clock at once: a fresh cooldown must not wait a tick to start its fill.
    setNow(Date.now());
    if (!cooling) return undefined;
    const timer = setInterval(() => setNow(Date.now()), SLOT_REDRAW_MS);
    return () => clearInterval(timer);
  }, [cooling, cooldowns]);
  const views: Record<string, AbilitySlotView> = {};
  for (const id of abilityIds) {
    const cost = costs?.find((c) => c.id === id)?.staminaCost;
    views[id] = abilitySlotView(cooldowns, id, Math.max(now, Date.now()), cost, stamina);
  }
  return views;
}

/** The class a slot carries for its look (`cd-cooling` / `cd-starved`, or none when ready). */
export function slotStateClass(view: AbilitySlotView | undefined): string {
  if (!view) return '';
  return `${view.state === 'cooling' ? ' cd-cooling' : ''}${view.starved ? ' cd-starved' : ''}`;
}

/**
 * The fill + the seconds label inside a slot: the Dodge button's bottom-up readiness fill, drawn
 * only while the ability cools, with the whole seconds left on top. Nothing while ready.
 */
export function AbilitySlotFill({
  view,
}: {
  view: AbilitySlotView | undefined;
}): JSX.Element | null {
  if (!view || view.state !== 'cooling') return null;
  return (
    <>
      <span
        className="slot-cd-fill"
        data-testid="slot-cd-fill"
        style={{ height: `${Math.round(view.readyPct * 100)}%` }}
      />
      <span className="slot-cd-left" data-testid="slot-cd-left">
        {view.leftS}
      </span>
    </>
  );
}
